import axios from 'axios';
import useAuthStore from '../store/authStore';
import { loginRequest } from '../authConfig';
import { msalEnabled, msalInstance } from '../authRuntime';

function normalizeApiBaseUrl(rawUrl) {
  if (!rawUrl) return rawUrl;
  return rawUrl.replace(/\/+$/, '').replace(/\/api(?:\/v1)?$/i, '');
}

function getDefaultApiBaseUrl() {
  if (typeof window === 'undefined') {
    // SSR / test environment — use http for local-only access.
    return 'http://127.0.0.1:8000';
  }

  const { protocol, hostname } = window.location;
  // Docker Compose maps backend host port 8001 -> container 8000 (see docker-compose.yml).
  // Always mirror the page's own scheme so HTTPS pages never generate http:// requests.
  return `${protocol}//${hostname}:8001`;
}

function isTokenExpired(token) {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Math.floor(Date.now() / 1000);
    // Return true if token is expired or expires in less than 5 minutes
    return payload.exp - now < 300;
  } catch (e) {
    return true;
  }
}

// REACT_APP_API_URL='' (explicitly set, e.g. behind an nginx reverse proxy) means
// "same origin" — an empty baseURL so requests resolve relative to the current host.
// REACT_APP_API_URL left unset (e.g. local `npm start`) falls back to the :8001 default.
function determineApiBaseUrl() {
  if (process.env.REACT_APP_API_URL !== undefined) {
    const normalized = normalizeApiBaseUrl(process.env.REACT_APP_API_URL);

    // Empty string case: nginx reverse proxy (same origin)
    if (!normalized) {
      return normalized;
    }

    // Non-empty case: check for Docker localhost-to-LAN remapping
    if (typeof window !== 'undefined') {
      try {
        const configuredUrl = new URL(normalized);
        const loopbackHosts = new Set(['localhost', '127.0.0.1', '[::1]']);
        const pageHost = window.location.hostname;
        if (loopbackHosts.has(configuredUrl.hostname) && !loopbackHosts.has(pageHost)) {
          configuredUrl.hostname = pageHost;
          // FIX: upgrade the scheme to match the page so that an http://localhost
          // env value never produces an http://production-host URL on an HTTPS page
          // (which the browser would block as mixed content → Axios 'Network Error').
          configuredUrl.protocol = window.location.protocol;
          return configuredUrl.toString().replace(/\/+$/, '');
        }
      } catch (_) {
        // Invalid URL, use as-is
      }
    }
    return normalized;
  }

  // Undefined: use default with Docker port 8001
  return getDefaultApiBaseUrl();
}

const api = axios.create({
  baseURL: determineApiBaseUrl(),
  timeout: 30000,
});

// Request interceptor: (1) attach Bearer token with silent refresh, (2) strip trailing
// slashes from path-only URLs so /api/v1/customers/ and /api/v1/customers resolve the
// same route and avoid spurious 307 redirects from nginx.
api.interceptors.request.use(async (config) => {
  // Normalize trailing slash on path-only URLs (no scheme = relative path).
  // Do NOT touch absolute URLs (e.g. MSAL exchange calls).
  if (config.url && !config.url.startsWith('http') && config.url.length > 1) {
    config.url = config.url.replace(/\/+$/, '');
  }

  const requestUrl = String(config.url || '');
  const publicEndpoints = ['/api/v1/auth/login', '/api/v1/auth/dev-login', '/api/v1/auth/callback', '/api/v1/auth/sso-login'];

  if (publicEndpoints.some(p => requestUrl.includes(p))) {
    return config;
  }

  let token = useAuthStore.getState().token;

  // Check if internal token is expired or missing
  if (token && isTokenExpired(token) && msalEnabled && msalInstance) {
    console.log('Token expired or expiring soon, attempting silent refresh...');
    try {
      const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
      if (account) {
        // 1. Refresh Azure ID Token
        const msalResponse = await msalInstance.acquireTokenSilent({
          ...loginRequest,
          account
        });

        // 2. Exchange for new internal JWT
        const exchangeResponse = await axios.post(`${api.defaults.baseURL}/api/v1/auth/sso-login`, {
          id_token: msalResponse.idToken
        });

        const newToken = exchangeResponse.data?.data?.access_token || exchangeResponse.data?.access_token;
        const newUser = exchangeResponse.data?.data?.user || exchangeResponse.data?.user;

        if (newToken) {
          useAuthStore.getState().setAuth(newToken, newUser);
          token = newToken;
          console.log('Silent refresh successful');
        }
      }
    } catch (err) {
      console.warn('Silent refresh failed, proceeding with existing token:', err);
    }
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => Promise.reject(error));

// Response interceptor: handle 401 redirect + classify transient vs permanent errors.
// Does NOT perform any automatic retries — retry logic lives in the callers
// (e.g. customerLookupService) so they can apply appropriate backoff/circuit-breaking.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = String(error?.config?.url || '');
    const isAuthEndpoint = requestUrl.startsWith('/api/v1/auth/');

    // 401 → clear session and redirect to login (existing behaviour, unchanged).
    if (error.response?.status === 401 && !isAuthEndpoint) {
      useAuthStore.getState().clearAuth();
      window.location.href = '/login';
    }

    // Classify the error so callers can make informed retry decisions.
    // We annotate the error object rather than throwing a custom class so that
    // all existing catch handlers continue to work without modification.
    const status = error.response?.status;
    if (!status) {
      // status is undefined/0 → browser-blocked (mixed content, CORS, offline).
      // These are configuration/environment errors. Retrying immediately will never
      // help, so mark the error so callers can detect this and circuit-break early.
      error._isPermanent = true;
      error._errorClass = 'NETWORK_BLOCK';
    } else if (status === 503 || status === 502 || status === 429) {
      // Transient server-side overload — callers should backoff before retrying.
      error._isTransient = true;
      error._errorClass = `HTTP_${status}`;
    } else if (status >= 400 && status < 500 && status !== 429) {
      // 4xx (except 429 Too Many Requests) are client errors — retrying won't help.
      error._isPermanent = true;
      error._errorClass = `HTTP_${status}`;
    }

    return Promise.reject(error);
  }
);

export function getNotifications(params = {}) {
  return api.get('/api/v1/notifications', { params });
}

let inFlightUnreadCountPromise = null;
let lastUnreadCountTimestamp = 0;
let cachedUnreadCountResponse = null;

export function getUnreadNotificationCount() {
  const now = Date.now();
  // Return cached result if called within 3 seconds
  if (cachedUnreadCountResponse && (now - lastUnreadCountTimestamp < 3000)) {
    return Promise.resolve(cachedUnreadCountResponse);
  }
  if (inFlightUnreadCountPromise) {
    return inFlightUnreadCountPromise;
  }
  inFlightUnreadCountPromise = api.get('/api/v1/notifications/unread-count')
    .then((res) => {
      cachedUnreadCountResponse = res;
      lastUnreadCountTimestamp = Date.now();
      inFlightUnreadCountPromise = null;
      return res;
    })
    .catch((err) => {
      inFlightUnreadCountPromise = null;
      throw err;
    });
  return inFlightUnreadCountPromise;
}

export function markNotificationRead(notificationId) {
  return api.post(`/api/v1/notifications/${notificationId}/mark-read`);
}

export function markAllNotificationsRead() {
  return api.post('/api/v1/notifications/mark-all-read');
}

export function submitAllocationCorrection(payload) {
  return api.post('/api/v1/employees/me/allocation-corrections', payload);
}

export function getAllocationCorrectionDetails(allocationHistoryId) {
  return api.get(`/api/v1/allocation-history/${allocationHistoryId}/correction`);
}

export function approveAllocationCorrection(allocationHistoryId) {
  return api.post(`/api/v1/allocation-history/${allocationHistoryId}/approve`);
}

export function approveIdtApplication(applicationId) {
  return api.post(`/api/v1/idt-applications/${applicationId}/approve`);
}

export function rejectIdtApplication(applicationId, body = {}) {
  return api.post(`/api/v1/idt-applications/${applicationId}/reject`, body);
}

export function updateIdtApplicationComment(applicationId, comment) {
  return api.put(`/api/v1/idt-applications/${applicationId}/comment`, { comment });
}

export function getEmployeeIdtApplications(employeeId) {
  const code = String(employeeId || '').trim();
  if (!code || ['n/a', 'na', 'none', 'null'].includes(code.toLowerCase())) {
    return Promise.reject(new Error('Valid employee id is required'));
  }
  return api.get(`/api/v1/employees/${encodeURIComponent(code)}/idt-applications`);
}

export function getMyIdtStatus() {
  return api.get('/api/v1/employees/me/idt/status');
}

export function getEmployeeIdtApplicationProgress(employeeId, applicationId) {
  return api.get(
    `/api/v1/employees/${encodeURIComponent(employeeId)}/idt-applications/${encodeURIComponent(applicationId)}/progress`
  );
}

export function getMyIdtApplicationProgress(applicationId) {
  return api.get(
    `/api/v1/employees/me/idt/applications/${encodeURIComponent(applicationId)}/progress`
  );
}

export function getTrainingRecords() {
  return api.get('/api/v1/training');
}

export function getMyAssessments() {
  return api.get('/api/v1/assessments/me');
}

export function getAssessmentQuestions(assessmentId) {
  return api.get(`/api/v1/assessments/${assessmentId}/questions`);
}

export function submitAssessment(assessmentId, answers) {
  return api.post(`/api/v1/assessments/${assessmentId}/submit`, { answers });
}

export function searchEmployeesForTraining(q, limit = 10) {
  return api.get('/api/v1/training/employees/search', { params: { q, limit } });
}

export function getTrainingMeta() {
  return api.get('/api/v1/training/meta');
}

export function createTrainingRecord(data) {
  return api.post('/api/v1/training', data);
}
export function getAllocationMeta() {
  return api.get('/api/v1/allocations/meta');
}

export function searchEmployees(query) {
  return api.get('/api/v1/employees', {
    params: {
      search: query,
      page_size: 10,
      include_management_fields: true,
    },
  });
}

export function searchProjects(query) {
  return api.get('/api/v1/projects/', { params: { search: query, page_size: 10 } });
}

// ── Employee management ───────────────────────────────────────────────────

export function getAllEmployees() {
  return api.get('/api/v1/employees/all', { timeout: 60000 });
}

export function getEmployeeDropdownData() {
  return api.get('/api/v1/employees/dropdown-data');
}

export function checkEmployeeIdExists(employeeId) {
  return api.get('/api/v1/employees/check-employee-id', {
    params: { employee_id: employeeId },
  });
}

export function createEmployee(data) {
  return api.post('/api/v1/employees', data);
}

export function markEmployeeExit(employeeId, data) {
  return api.post(`/api/v1/employees/${employeeId}/mark-exit`, data);
}

export function cancelEmployeeExit(employeeId) {
  return api.post(`/api/v1/employees/${employeeId}/cancel-exit`);
}

export function completeEmployeeExit(employeeId, data) {
  return api.post(`/api/v1/employees/${employeeId}/complete-exit`, data || {});
}

export function extendEmployeeLwd(employeeId, data) {
  return api.patch(`/api/v1/employees/${employeeId}/extend-lwd`, data);
}

export function reactivateEmployee(employeeId, data) {
  return api.post(`/api/v1/employees/${employeeId}/reactivate`, data);
}

export default api;
